import { Navigate } from 'react-router';

import { HomeRoutes, StudioRoutes } from '../../../../RouteConstants';
import { IRoutes } from '@components/3.0/Layouts/TabbedLayout/utils/constants/routes.interface';
import { ErrorBoundary, RouteErrorPage } from '@components/ErrorBoundary';
import Features from '@Features/feature-flags';
import { editQuestionRoutes } from './studio.editQuestion.routes';
import { editDomainRoutes } from './studio.editDomain.routes';
import { LoadingIndicator } from '@components/LoadingIndicatorMaterial';
import { notFoundRoute } from './404.routes';

export const routes: IRoutes[] = [
  ...editDomainRoutes,
  {
    routeItem: {
      path: `${StudioRoutes.questions.base}`,
      children: [
        notFoundRoute.routeItem,
        {
          path: '',
          hydrateFallbackElement: <LoadingIndicator isLoading={true} />,
          async lazy() {
            let Questions = (await import('Modules/Studio/QueriesList/Questions')).default;
            const ToggleComponentBasedOnPermission = (
              await import('RBAC/ToggleComponentBasedOnPermission')
            ).default;

            return {
              element: (
                <ToggleComponentBasedOnPermission
                  IsAllowed={Questions}
                  IsNotAllowed={() => <Navigate to={`/${HomeRoutes.notAuthorized}`} />}
                  permissionRef={StudioRoutes.questions.permission}
                />
              ),
              errorElement: <RouteErrorPage />,
            };
          },

          children: [
            {
              path: '',
              hydrateFallbackElement: <LoadingIndicator isLoading={true} />,
              async lazy() {
                let DomainsHOC = (
                  await import('Modules/Studio/QueriesList/components/DomainsList/DomainsHOC')
                ).default;
                const ToggleComponentBasedOnPermission = (
                  await import('RBAC/ToggleComponentBasedOnPermission')
                ).default;

                return {
                  element: (
                    <ToggleComponentBasedOnPermission
                      IsAllowed={DomainsHOC}
                      IsNotAllowed={() => <Navigate to={`/${HomeRoutes.notAuthorized}`} />}
                      permissionRef={StudioRoutes.questions.permission}
                    />
                  ),
                  errorElement: <RouteErrorPage />,
                };
              },
            },
            {
              path: StudioRoutes.questions.basePathVariables,
              hydrateFallbackElement: <LoadingIndicator isLoading={true} />,
              async lazy() {
                const QuestionsList = (
                  await import('Modules/Studio/QueriesList/components/QuestionsList/QuestionsList')
                ).default;
                const ToggleComponentBasedOnPermission = (
                  await import('RBAC/ToggleComponentBasedOnPermission')
                ).default;

                return {
                  element: (
                    <ToggleComponentBasedOnPermission
                      IsAllowed={QuestionsList}
                      IsNotAllowed={() => <Navigate to={`/${HomeRoutes.notAuthorized}`} />}
                      permissionRef={StudioRoutes.questions.permission}
                    />
                  ),
                  errorElement: <RouteErrorPage />,
                };
              },
            },
          ],
        },
      ],
    },
    permissionRef: StudioRoutes.questions.permission,
    featureFlag: true,
  },
  ...editQuestionRoutes,
  {
    routeItem: {
      path: `${StudioRoutes.dataSources.base}`,
      children: [
        {
          path: '*',
          hydrateFallbackElement: <LoadingIndicator isLoading={true} />,
          errorElement: <RouteErrorPage />,
          async lazy() {
            let DataSourcePage = (await import('Modules/Studio/DataSources/DataSourcePage'))
              .default;
            const ToggleComponentBasedOnPermission = (
              await import('RBAC/ToggleComponentBasedOnPermission')
            ).default;

            return {
              element: (
                <ToggleComponentBasedOnPermission
                  IsAllowed={() => (
                    <ErrorBoundary>
                      <DataSourcePage />
                    </ErrorBoundary>
                  )}
                  IsNotAllowed={() => <Navigate to={`/${HomeRoutes.notAuthorized}`} />}
                  permissionRef={StudioRoutes.dataSources.permission}
                />
              ),
              errorElement: <RouteErrorPage />,
            };
          },
        },
      ],
    },
    permissionRef: StudioRoutes.dataSources.permission,
    featureFlag: true,
  },
  {
    routeItem: {
      path: 'chat',
      children: [
        {
          path: '*',
          hydrateFallbackElement: <LoadingIndicator isLoading={true} />,
          async lazy() {
            const AIView = (await import('Modules/Studio/AI/AI')).default;

            return {
              element: <AIView />,
              errorElement: <RouteErrorPage />,
            };
          },
        },
      ],
    },
    permissionRef: StudioRoutes.createQuestion.permission,
    featureFlag: (Features as any).aiChatPoc,
  },
  {
    routeItem: {
      path: StudioRoutes.api.base,
      hydrateFallbackElement: <LoadingIndicator isLoading={true} />,
      async lazy() {
        let ApiDetails = (await import('Modules/Studio/ApiListView/ApiListView')).default;
        const ToggleComponentBasedOnPermission = (
          await import('RBAC/ToggleComponentBasedOnPermission')
        ).default;

        return {
          element: (
            <ToggleComponentBasedOnPermission
              IsAllowed={() => (
                <ErrorBoundary>
                  <ApiDetails />
                </ErrorBoundary>
              )}
              IsNotAllowed={() => <Navigate to={`/${HomeRoutes.notAuthorized}`} />}
              permissionRef={StudioRoutes.api.permission}
            />
          ),
          errorElement: <RouteErrorPage />,
        };
      },
    },
    permissionRef: StudioRoutes.api.permission,
    featureFlag: true,
  },

  {
    routeItem: {
      path: StudioRoutes.auditLog.base,
      hydrateFallbackElement: <LoadingIndicator isLoading={true} />,
      async lazy() {
        let AuditLog = (await import('Modules/Studio/AuditLog/AuditLog')).default;
        const ToggleComponentBasedOnPermission = (
          await import('RBAC/ToggleComponentBasedOnPermission')
        ).default;

        return {
          element: (
            <ToggleComponentBasedOnPermission
              IsAllowed={() => (
                <ErrorBoundary>
                  <AuditLog />
                </ErrorBoundary>
              )}
              IsNotAllowed={() => <Navigate to={`/${HomeRoutes.notAuthorized}`} />}
              permissionRef={StudioRoutes.auditLog.permission}
            />
          ),
          errorElement: <RouteErrorPage />,
        };
      },
    },
    permissionRef: StudioRoutes.auditLog.permission,
    featureFlag: true,
  },
  {
    routeItem: {
      path: StudioRoutes.ingestion.base,
      hydrateFallbackElement: <LoadingIndicator isLoading={true} />,
      async lazy() {
        let IsStudioHOC = (await import('Modules/Studio/components/StudioInfo/IsStudioHOC'))
          .default;
        let IngestionPage = (
          await import('Modules/Studio/components/StudioInfo/Ingestion/IngestionPage')
        ).default;
        const ToggleComponentBasedOnPermission = (
          await import('RBAC/ToggleComponentBasedOnPermission')
        ).default;

        return {
          element: (
            <ToggleComponentBasedOnPermission
              IsAllowed={() => (
                <ErrorBoundary>
                  <IsStudioHOC>
                    <IngestionPage />
                  </IsStudioHOC>
                </ErrorBoundary>
              )}
              IsNotAllowed={() => <Navigate to={`/${HomeRoutes.notAuthorized}`} />}
              permissionRef={StudioRoutes.ingestionLog.permission}
            />
          ),
          errorElement: <RouteErrorPage />,
        };
      },
    },
    permissionRef: StudioRoutes.ingestionLog.permission,
    featureFlag: true,
  },

  notFoundRoute,
];
